import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import * as React from "react";
import AddableList from "../common/AddableList";
import FieldError from "../common/FieldError";
import {MigrationType} from "../../models/Statistics/MigrationType";
import {DropboxRootFolder} from "../../models/ClientConfiguration/DropboxRootFolder";

const migrationTypeSelectOptions: { id: MigrationType; label: string }[] = [
  { id: MigrationType.CasesMigration, label: "Cases" },
  { id: MigrationType.IntakesMigration, label: "Intakes" },
  { id: MigrationType.ProvidersMigration, label: "Providers" },
  { id: MigrationType.FirmDocumentsMigration, label: "Firm Documents" },
  { id: MigrationType.TemplatesMigration, label: "Template Files" },
];

interface DropboxRootFoldersConfigurationControlProps {
  dropboxRootFolders: DropboxRootFolder[];
  onChange: (folders: DropboxRootFolder[]) => void;
  disabled?: boolean;
}

const DropboxRootFoldersConfigurationControl: React.FC<DropboxRootFoldersConfigurationControlProps> = ({
  dropboxRootFolders,
  onChange,
  disabled = false
}) => {
  const handleAddClick = () => {
    const newRootFolder: DropboxRootFolder = {
      folderPath: "",
      migrationType: MigrationType.CasesMigration,
    };
    const updatedRootFolders = [...dropboxRootFolders, newRootFolder];
    onChange(updatedRootFolders);
  };

  const handleKeyPress = (
    event: React.KeyboardEvent<HTMLInputElement>,
    _: number,
  ) => {
    if (event.key === "Enter") {
      event.preventDefault();
    }
  };

  const handleChangeRootFolder = (index: number, value: string) => {
    let formattedValue = value.trim();
    formattedValue = formattedValue.replace(/\\/g, "/");
    if (!formattedValue.startsWith("/"))
      formattedValue = "/" + formattedValue;

    const updatedRootFolders = [...dropboxRootFolders];
    updatedRootFolders[index].folderPath = formattedValue;
    onChange(updatedRootFolders);
  };


  const handleChangeRootFolderMigrationType = (index: number, value: MigrationType) => {
    const updatedRootFolders = [...dropboxRootFolders];
    updatedRootFolders[index].migrationType = value;
    onChange(updatedRootFolders);
  };

  const handleDeleteRootFolder = (index: number) => {
    const updatedRootFolders = [...dropboxRootFolders];
    updatedRootFolders.splice(index, 1);
    onChange(updatedRootFolders);
  };

  return (
    <div>
      <AddableList
        onAddClick={handleAddClick}
        subheaderText="Root Folders"
        disabled={disabled}
      >
        {dropboxRootFolders.map((rootFolder, index) => {
          return (
            <div key={index} className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
              <div className="grid grid-cols-12 gap-2 w-full">
                <div className="col-span-9">
                  <Input
                    className="h-8"
                    value={rootFolder.folderPath ?? ""}
                    onChange={(event) => handleChangeRootFolder(index, event.target.value)}
                    onKeyDown={(event) => handleKeyPress(event as React.KeyboardEvent<HTMLInputElement>, index)}
                    disabled={disabled}
                  />
                </div>
                <div className="col-span-3">
                  <Select
                    value={rootFolder.migrationType}
                    onValueChange={(value) => handleChangeRootFolderMigrationType(index, value as MigrationType)}
                    disabled={disabled}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select migration type" />
                    </SelectTrigger>
                    <SelectContent>
                      {migrationTypeSelectOptions.map((option) => (
                        <SelectItem key={option.id} value={option.id}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button
                aria-label="Delete"
                variant="ghost"
                size="icon-sm"
                className="text-destructive hover:text-destructive"
                onClick={() => handleDeleteRootFolder(index)}
                disabled={disabled}
              >
                <Trash2 className="size-4" />
              </Button>
              </div>
              <FieldError name={`dropboxRootFolders.${index}.folderPath`} />
            </div>
          );
        })}
      </AddableList>
    </div>
  );
};

export default DropboxRootFoldersConfigurationControl;
